import { useMemo } from "react";
import { Link } from "react-router-dom";
import { LanguageSelector } from "@/components/LanguageSelector";
import { LocationSelector } from "@/components/LocationSelector";
import { Linkedin } from "lucide-react";
import { useLocalizations } from "@/hooks/useLocalizations";
import { useLocalizedRoutes } from "@/hooks/useLocalizedRoutes";
import { useUserRole } from "@/hooks/useUserRole";
import { LCommon, LPages } from "@/components/i18n";
import { RESPONSIVE, TYPOGRAPHY, SPACING } from "@/design-system";
import { cn } from "@/lib/utils";

const Footer = () => {
  const { lPages, lCommon } = useLocalizations();
  const { getPath, getPagePath } = useLocalizedRoutes();
  const { isSuperAdmin } = useUserRole();
  const currentYear = new Date().getFullYear();
  
  
  // Helper to render section title with explicit fallbacks
  const renderSectionTitle = (titleKey: string) => {
    if (titleKey === 'footer.about') return <LCommon id="footer.about" defaultValue='Platform' />;
    if (titleKey === 'footer.discover') return <LCommon id="footer.discover" defaultValue="Discover" />;
    if (titleKey === 'footer.support') return <LCommon id="footer.support" defaultValue="Support" />;
    return null;
  };

  // Helper to render link label with explicit fallbacks
  const renderLinkLabel = (labelKey: string) => {
    if (labelKey === 'nav.about_us') return <LCommon id="nav.about_us" defaultValue="About Us" />;
    if (labelKey === 'nav.methodology') return <LCommon id="nav.methodology" defaultValue="Methodology" />;
    if (labelKey === 'nav.demo_video') return <LCommon id="nav.demo_video" defaultValue="Demo Video" />;
    if (labelKey === 'nav.experts') return <LCommon id="nav.experts" defaultValue="Experts" />;
    if (labelKey === 'nav.insights') return <LCommon id="nav.insights" defaultValue="Insights" />;
    if (labelKey === 'nav.explore') return <LCommon id="nav.explore" defaultValue="Explore" />;
    if (labelKey === 'nav.account') return <LCommon id="nav.account" defaultValue="Account" />;
    if (labelKey === 'nav.plans') return <LCommon id="nav.plans" defaultValue="Plans" />;
    if (labelKey === 'nav.help_center') return <LCommon id="nav.help_center" defaultValue="Help Center" />;
    if (labelKey === 'footer.legal.terms_of_use') return <LCommon id="footer.legal.terms_of_use" defaultValue="Terms of Use" />;
    if (labelKey === 'footer.legal.privacy_policy') return <LCommon id="footer.legal.privacy_policy" defaultValue="Privacy Policy" />;
    return null;
  };

  // Routes vs Pages - use appropriate getter for each type
  // Routes: experts, insights, explore, account, help (system routes)
  // Pages: about-us, methodology, demo (institutional pages)
  const footerSections = useMemo(() => {
    const aboutLinks = [
      { labelKey: 'nav.about_us', routeKey: "about-us", isPage: true },
      { labelKey: 'nav.methodology', routeKey: "methodology", isPage: true },
      { labelKey: 'nav.demo_video', routeKey: "demo", isPage: true },
      ...(isSuperAdmin ? [{ labelKey: 'nav.plans', routeKey: "plans", isPage: false }] : []),
    ];
    return [
    {
      titleKey: 'footer.about',
      links: aboutLinks,
    },
    {
      titleKey: 'footer.discover',
      links: [
        { labelKey: 'nav.experts', routeKey: "experts", isPage: false },
        { labelKey: 'nav.explore', routeKey: "explore", isPage: false },
      ],
    },
    {
      titleKey: 'footer.support',
      links: [
        { labelKey: 'nav.account', routeKey: "account", isPage: false },
        { labelKey: 'nav.help_center', routeKey: "help", isPage: false },
      ],
    },
  ];
  }, [isSuperAdmin]);

  const legalLinks = [
    { labelKey: "footer.legal.terms_of_use", routeKey: "terms-of-use" },
    { labelKey: "footer.legal.privacy_policy", routeKey: "privacy-policy" },
  ];

  return (
    <footer className="border-t border-border bg-muted/30 pb-20 md:pb-0">
      <div className="container py-12 md:py-16 relative">
        <div className={cn("grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4", RESPONSIVE.gapResponsiveLgXl)}>
          {/* Link Columns */}
          {footerSections.map((section) => (
            <div key={section.titleKey} className="text-center sm:text-left">
              <h3 className={cn(SPACING.sectionHeader, 'font-semibold text-sm')}>{renderSectionTitle(section.titleKey)}</h3>
              <ul className={cn(SPACING.stackMd)}>
                {section.links.map((link) => (
                  <li key={link.labelKey}>
                    <Link
                      to={link.isPage ? getPagePath(link.routeKey) : getPath(link.routeKey)}
                      className={cn(TYPOGRAPHY.cardDescription, 'hover:text-primary transition-colors')}
                    >
                      {renderLinkLabel(link.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {/* Legal Column */}
          <div className="text-center sm:text-left">
            <h3 className={cn(SPACING.sectionHeader, 'font-semibold text-sm')}><LCommon id="footer.legal.title" defaultValue='Legal' /></h3>
            <ul className={cn(SPACING.stackMd)}>
              {legalLinks.map((link) => (
                <li key={link.routeKey}>
                  <Link
                    to={getPagePath(link.routeKey)}
                    className={cn(TYPOGRAPHY.cardDescription, 'hover:text-primary transition-colors')}
                  >
                    {renderLinkLabel(link.labelKey)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={cn("mt-12 pt-8 border-t border-border", RESPONSIVE.footerBottomBar)}>
          <div className={cn("flex items-center", RESPONSIVE.gapResponsiveXs)}>
            <a
              href="http://linkedin.com/company/tributario"
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 rounded-lg bg-foreground hover:bg-foreground/90 transition-colors shrink-0"
              aria-label={lCommon('aria.visit_linked_in', { defaultValue: 'Visit LinkedIn' })}
            >
              <Linkedin className="h-4 w-4 text-background" />
            </a>
            <p className={cn(RESPONSIVE.textResponsiveXs, "text-muted-foreground")}>
              <LPages
                id="home.footer.copyright"
                values={{ year: currentYear }}
                defaultValue='© {{year}} Tax.Network. All rights reserved.' />
            </p>
          </div>
          <div className={cn(RESPONSIVE.metadataRow, TYPOGRAPHY.cardDescription)}>
            <LocationSelector variant="footer" />
            <LanguageSelector variant="footer" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;